'use client';

import type { PersistedHandle } from '@bomdisoft/store/persisted/types';
import { useCallback, useEffect, useSyncExternalStore } from 'react';

/**
 * Subscribe a component to a persisted value.
 *
 * The server snapshot is the default, because there is no address to read on
 * the server here and no storage anywhere but the browser. `useSyncExternalStore`
 * serves that during hydration and re-renders with the client value straight
 * after, which is what keeps the first paint agreeing with the server.
 *
 * Use this for any handle without a `url` sink, and for `url` handles in a
 * React app that is not server rendered. A Next screen wants the `/next` entry
 * point, whose server snapshot carries the address.
 */
export const usePersisted = <T>(handle: PersistedHandle<T>, scope?: string): [T, (next: T | ((current: T) => T)) => void] => {
  const subscribe = useCallback((listener: () => void) => handle.subscribe(listener, scope), [handle, scope]);
  const snapshot = useCallback(() => handle.read(scope), [handle, scope]);
  const serverSnapshot = useCallback(() => handle.fromUrl(null, scope), [handle, scope]);
  const value = useSyncExternalStore(subscribe, snapshot, serverSnapshot);

  useEffect(() => {
    handle.reflect(value, scope);
  }, [handle, scope, value]);

  const write = useCallback((next: T | ((current: T) => T)) => handle.write(next, scope), [handle, scope]);
  return [value, write];
};
